import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchCustomers } from '../../slices/customers/customersSlice';
import { EmployeesCreateModal } from '../company-create/components/modal/EmployeesCreateModal';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { Customer } from '../../models/customer.model';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './CustomerProfiles.module.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { NWClient } from '../../client/NWClient';
import MessageModal from '../../common/message-modal/MessageModal';
import { useCustomerProfiles } from './hooks/useCustomerProfiles';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import { CustomerRow } from './components/CustomerRow';
import { CustomersTable } from './components/CustomersTable';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import Preview from './components/preview-block/Preview';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import { Helpers } from '../../common/helpers/helpers';

const CustomerProfiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //const [pendingStatus, setPendingStatus] = useState(true);
  const [isOpenCustomersCreation, setIsOpenCustomersCreation] = useState(false);
  const [planMessageVisible, setPlanMessageVisible] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);

  const {
    customers: customersCreatedByAdmin,
    setCurrentPage,
    customersCount,
    currentPage,
    getRequestedPage,
    cardView,
    setCardView,
    customersLoading,
    resetSearchFilter,
    searchCleared,
    setSearchCleared,
    setSearchStringInputValue,
    searchStringInputValue,
  } = useCustomerProfiles();

  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(customersCount / itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page);
  };

  const handleClose = () => {
    setIsOpenCustomersCreation(false);
  };
  const company = useAppSelector(selectAllCompanies)[0];

  const [customerId, setCustomerId] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setCustomerId(id);
  };
  const handleMoreVertClose = () => {
    setAnchorEl(null);
  };

  const checkPermission = useCheckPermission();

  const handleProfileDelete = () => {
    if (customerId) {
      setAnchorEl(null);
      setDeleteMessageModalOpen(false);
      NWClient.delete('customer', customerId).then(() => {
        setCustomerId(null);
        dispatch(fetchCustomers({ company_id: company?.id }));
      });
    }
  };

  const handleEdit = (id: number) => {
    navigate(ROUTES.CUSTOMER_PROFILES + '/' + id + '?initialTab=1');
  };

  const handleDocuments = (id: number) => {
    navigate(ROUTES.CUSTOMER_PROFILES + '/' + id + '?initialTab=0');
  };

  const handleDelete = (id: number) => {
    setCustomerId(id);
    setDeleteMessageModalOpen(true);
  };

  const handleCreateCustomer = () => {
    const planSettings = Helpers.getPlanSettings(company);
    if (!planSettings) return;

    const limit = planSettings.customer_profile;
    const count = customersCreatedByAdmin?.length ?? 0;

    if (limit === 0) {
      setPlanMessageVisible(true);
    } else if (limit !== 'unlimited' && count >= limit) {
      setPlanMessageVisible(true);
    } else {
      navigate(ROUTES.CUSTOMER_PROFILE_CREATE);
    }
  };

  /*useEffect(() => {
    if (company) {
      dispatch(fetchCustomers({ company_id: company?.id }));
      setPendingStatus(false);
    } else {
      dispatch(fetchCompanies())
        .unwrap()
        .finally(() => setPendingStatus(false));
    }
  }, [company, dispatch]);*/

  return (
    <Grid container sx={{ mt: '-61px' }}>
      <Box>
        <Typography variant='h1' component='h1' className={styles.page_title}>
          <span style={{ marginRight: '8px' }}>{t('pages.customerProfiles')}</span>
          <PreviewTooltip title={<Preview />} arrow placement='top'>
            <InfoIcon color='primary' fontSize='small' />
          </PreviewTooltip>
        </Typography>
      </Box>
      <Box display='flex' mt='8px' width='100%' justifyContent='space-between' alignItems='center'>
        {(customersCount && customersCount) > 20 ? (
          <Button
            onClick={handleCreateCustomer}
            className='btn-default btn-action-basic'
            variant='contained'
            color='primary'
            style={{ flexShrink: 0 }}
          >
            <Typography fontWeight='light' fontSize={14}>
              {t('buttons.addProfile')}
            </Typography>
          </Button>
        ) : null}
        <Box display={'flex'} alignItems={'center'} justifyContent={'flex-start'} gap={'8px'}>
          <TextField
            label={t('labels.searchByName')}
            className='search-input'
            InputLabelProps={{ style: { backgroundColor: '#f4f9fd' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    disabled={!searchStringInputValue.trim()}
                    onClick={() => resetSearchFilter()}
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={searchStringInputValue}
            onChange={(e) => {
              setSearchStringInputValue(e.target.value);
              if (e.target.value.trim().length > 0) {
                setSearchCleared(false);
              }
            }}
            onKeyUp={(e) => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              if (e.target.value.trim() === '' && !searchCleared) {
                resetSearchFilter();
              }
            }}
          />
          {customersCount && customersCount > 20 ? (
            <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography className='pagination-pages-value' variant='caption'>
                Page {currentPage} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                siblingCount={0}
                boundaryCount={0}
                sx={{
                  '& .MuiPagination-ul': { gap: 0 },
                }}
              />
            </Box>
          ) : null}
          <Box className={styles.card_list_view_container}>
            <button
              className={`${styles.variant_btn} + ${cardView ? styles.variant_btn_active : ''}`}
              onClick={() => setCardView(true)}
            >
              <DashboardIcon />
              <span>{t('buttons.card')}</span>
            </button>
            <button
              onClick={() => {
                setCardView(false);
              }}
              className={`${styles.variant_btn} + ${!cardView ? styles.variant_btn_active : ''}`}
            >
              <BallotIcon />
              <span>{t('buttons.list')}</span>
            </button>
          </Box>
        </Box>
      </Box>
      <Grid size={{ xs: 12 }} className={styles.customer_cards}>
        {customersLoading === 'loading' && <ProgressOverlay className='position-absolute' />}
        {cardView ? (
          <>
            {[...customersCreatedByAdmin]
              .sort((a, b) => a.customer_name.localeCompare(b.customer_name))
              .map((customer: Customer) => (
                <Card
                  tabIndex={-1}
                  key={customer.id}
                  onClick={() => navigate(ROUTES.CUSTOMER_PROFILES + '/' + customer.id)}
                  className={styles.customer_card}
                >
                  {customer.customer_name && (
                    <Typography
                      fontWeight='regular'
                      className={`${styles.customer_name} ${
                        customer.customer_name && customer.customer_name.length > 30
                          ? styles.long_text
                          : ''
                      }`}
                      title={customer.customer_name}
                    >
                      {customer.customer_name}
                    </Typography>
                  )}
                  <Box className={styles.customer_profile_icons}>
                    {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CREATE) ||
                    checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE_DOCUMENT) ||
                    checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_DELETE_DOCUMENT) ? (
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDocuments(customer.id);
                        }}
                        size='small'
                      >
                        <UploadFileOutlinedIcon fontSize='small' />
                      </IconButton>
                    ) : (
                      <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
                        <IconButton disabled={true} onClick={null} size='small'>
                          <UploadFileOutlinedIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                    {checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE) ? (
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEdit(customer.id);
                        }}
                        size='small'
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                    ) : (
                      <Tooltip title={t('messages.contactAdministrator')}>
                        <IconButton onClick={null} disabled={true} size='small' sx={{ p: 0.5 }}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                      aria-label={t('labels.menuButton')}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClick(event, customer.id);
                      }}
                      size='small'
                      sx={{ p: 0.5 }}
                    >
                      <MoreVertIcon fontSize='small' />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            <Button
              onClick={handleCreateCustomer}
              variant='contained'
              color='primary'
              disabled={!checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_CREATE)}
              className={styles.add_button}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '1rem' }}
              >
                <Typography fontWeight='light' fontSize={14}>
                  {t('buttons.addProfile')}
                </Typography>
                <AddIcon />
              </Box>
            </Button>
          </>
        ) : (
          <CustomersTable>
            {[...customersCreatedByAdmin].map((customer) => (
              <CustomerRow
                customer={customer}
                key={customer.id}
                handleDelete={() => handleDelete(customer.id)}
                handleEdit={() => handleEdit(customer.id)}
                navigateToDocs={() => handleDocuments(customer.id)}
              />
            ))}
          </CustomersTable>
        )}
        {customersCount && customersCount > 20 ? (
          <Box
            display='flex'
            alignItems='center'
            justifyContent='flex-end'
            marginTop='8px'
            width='100%'
          >
            <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography className='pagination-pages-value' variant='caption'>
                Page {currentPage} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                siblingCount={0}
                boundaryCount={0}
                sx={{
                  '& .MuiPagination-ul': { gap: 0 },
                }}
              />
            </Box>
          </Box>
        ) : null}
      </Grid>
      <EmployeesCreateModal open={isOpenCustomersCreation} onClose={handleClose} />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMoreVertClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          disabled={!checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_DELETE)}
          onClick={() => setDeleteMessageModalOpen(true)}
        >
          {t('buttons.delete')}
        </MenuItem>
      </Menu>

      {/*pendingStatus ? <ProgressOverlay /> : ''*/}
      {planMessageVisible && (
        <MessageModal
          open={planMessageVisible}
          modalMessage={t('messages.limitCustomers')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => navigate(ROUTES.PRICING)}
          goBackButtonText={t('buttons.later')}
          onClose={() => {
            setPlanMessageVisible(false);
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('messages.deleteCustomer')}
          modalMessage={t('messages.deleteCustomerDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => setDeleteMessageModalOpen(false)}
          goBackButtonText={t('buttons.delete')}
          onClose={() => {
            handleProfileDelete();
          }}
        />
      )}
    </Grid>
  );
};

export default CustomerProfiles;
